import { jwtDecode, JwtPayload } from 'jwt-decode'
import store from '@/plugins/store'
import { AxiosClient } from '@/app/common/services/http/axios-client.service'
import { AuthData } from '@/app/auth/entities/auth-data.entity'

const httpClient = new AxiosClient()

interface TokenTimeoutServiceInterface {
    setTokenTimeout: (accessToken: string, restartCounter: boolean) => void
    clearTokenTimeout: () => void
}

class TokenTimeoutService implements TokenTimeoutServiceInterface {
    private timeoutId: number | null = null
    private readonly bufferTime = 30000 // 30 seconds buffer before expiration
    private retryCount = 0
    private readonly maxRetries = 25

    setTokenTimeout(accessToken: string, restartCounter = false): void {

        if (restartCounter) {
            this.retryCount = 0
        }

        this.clearTokenTimeout() // Clear any existing timeout

        try {
            const decodedToken = jwtDecode(accessToken) as JwtPayload
            const expirationTime = decodedToken.exp

            if (!expirationTime) {
                return
            }

            const currentTime = Math.floor(Date.now() / 1000)
            const timeUntilExpiration = (expirationTime - currentTime) * 1000 // Convert to milliseconds
            const timeoutDuration = Math.max(0, timeUntilExpiration - this.bufferTime)

            this.timeoutId = setTimeout(async () => {
                await this.refreshToken()
                this.timeoutId = null
            }, timeoutDuration)
        } catch (error) {
            // Silently handle token parsing errors
        }
    }

    clearTokenTimeout(): void {
        if (this.timeoutId) {
            clearTimeout(this.timeoutId)
            this.timeoutId = null
        }
    }

    private async refreshToken(): Promise<void> {
        if (this.retryCount >= this.maxRetries) {
            // Max retries reached, stop attempting to refresh
            return
        }

        this.retryCount++

        try {
            const refreshToken = store.state.auth.refreshToken

            if (!refreshToken) {
                return
            }

            const response = await httpClient.post('/api/auth/refresh', { refreshToken })
            const authData = response.asEntity(AuthData)

            // Update tokens in store
            store.dispatch('auth/updateTokens', {
                accessToken: authData.accessToken,
                refreshToken: authData.refreshToken
            })
        } catch (error) {
            // Let the existing refresh token middleware handle the failure
        }
    }
}

// Export singleton instance
export const tokenTimeoutService = new TokenTimeoutService()
export default tokenTimeoutService
